import { type ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingScreen } from '@/shared/components/feedback/LoadingScreen';
import { useAuth } from '../hooks/useAuth';

interface PublicRouteProps {
  readonly children: ReactNode;
  readonly redirectTo?: string;
}

/**
 * PublicRoute - Route guard for unauthenticated users only
 *
 * Features:
 * - Redirects authenticated users away from auth pages (login, register)
 * - Prevents logged-in users from accessing login/register pages
 * - Shows loading screen until the initial session check has finished
 * - Uses redirectPath from store or falls back to default
 * - Clears redirectPath after successful navigation
 *
 * @example
 * <PublicRoute redirectTo="/dashboard">
 *   <LoginPage />
 * </PublicRoute>
 */
export function PublicRoute({
  children,
  redirectTo = '/dashboard',
}: PublicRouteProps) {
  const { isAuthenticated, isInitialized, redirectPath, setRedirectPath } =
    useAuth();
  const location = useLocation();
  const hasClearedRedirectPath = useRef(false);

  // Clear redirect path in useEffect to avoid setState during render
  useEffect(() => {
    // Until the session check finishes, `isAuthenticated` is only the
    // rehydrated value — do not act on it.
    if (!isInitialized) {
      return;
    }

    if (isAuthenticated && redirectPath && !hasClearedRedirectPath.current) {
      setRedirectPath(null);
      hasClearedRedirectPath.current = true;
    }

    // Reset flag when not authenticated
    if (!isAuthenticated) {
      hasClearedRedirectPath.current = false;
    }
  }, [isAuthenticated, isInitialized, redirectPath, setRedirectPath]);

  // Same gate as ProtectedRoute: `isAuthenticated` is rehydrated from storage
  // and means nothing until `checkAuth` has run, so a stale value would
  // bounce a signed-out user away from the login page they need.
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // Authenticated user trying to access public route (login, register, etc.)
  if (isAuthenticated) {
    // Get the intended destination or use default
    const destination = redirectPath ?? redirectTo;

    return <Navigate to={destination} state={{ from: location }} replace />;
  }

  // Not authenticated - render public content (login, register, etc.)
  return <>{children}</>;
}
